// src/components/ServiceDistributionDashboard.jsx
import React from 'react';
import { useMonthlyServiceDistribution } from '../../hooks/useAppointmentsPG';
import ServiceDistributionChart from './ServiceDistributionChart';

const ServiceDistributionDashboard = () => {
  const { data, loading, error, refresh, lastFetched } = useMonthlyServiceDistribution();

  // Calculate summary stats
  const stats = React.useMemo(() => {
    if (!data || data.length === 0) return null;

    const months = [...new Set(data.map(row => row.month))];
    const services = [...new Set(data.map(row => row.service_name))];
    const totalAppointments = data.reduce((sum, row) => sum + parseInt(row.appointment_count), 0);

    // Find most popular service overall
    const serviceStats = {};
    data.forEach(row => {
      if (!serviceStats[row.service_name]) {
        serviceStats[row.service_name] = 0;
      }
      serviceStats[row.service_name] += parseInt(row.appointment_count);
    });

    const mostPopularService = Object.entries(serviceStats)
      .sort(([, a], [, b]) => b - a)[0];

    return {
      monthsCount: months.length,
      servicesCount: services.length,
      totalAppointments,
      mostPopularService: mostPopularService ? mostPopularService[0] : 'N/A',
      mostPopularServiceCount: mostPopularService ? mostPopularService[1] : 0,
      dateRange: months.length > 0 ? {
        start: months[0],
        end: months[months.length - 1]
      } : null
    };
  }, [data]);

  return (
    <div className="w-full bg-white rounded-xl shadow-sm border border-gray-100 p-2 sm:p-4 lg:p-6">
      {/* Header */}
      <div className="rounded-xl p-6 border border-purple-100/50">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              התפלגות שירותים חודשית
            </h1>
            <p className="text-gray-600">
              עקבי אחר איך תמהיל השירותים שלך מתפתח מדי חודש וזהי טיפולים פופולריים
            </p>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">{stats.monthsCount}</div>
            <div className="text-sm text-gray-600">חודשים במעקב</div>
            {stats.dateRange && (
              <div className="text-xs text-gray-500 mt-1">
                {stats.dateRange.start} to {stats.dateRange.end}
              </div>
            )}
          </div>

          <div className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-cyan-600">{stats.servicesCount}</div>
            <div className="text-sm text-gray-600">שירותים פעילים</div>
          </div>

          <div className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg">
            <div className="text-2xl font-bold text-emerald-600">{stats.totalAppointments.toLocaleString()}</div>
            <div className="text-sm text-gray-600">סה"כ תורים</div>
          </div>

          <div className="text-center p-2 sm:p-3 bg-gray-50 rounded-lg">
            <div className="text-sm font-semibold text-amber-600 truncate" title={stats.mostPopularService}>
              {stats.mostPopularService}
            </div>
            <div className="text-sm text-gray-600">הכי פופולרי</div>
            <div className="text-xs text-gray-500 mt-1">
              {stats.mostPopularServiceCount} תורים
            </div>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="mt-6">
        <ServiceDistributionChart
          data={data}
          loading={loading}
          error={error}
        />
      </div>
    </div>
  );
};

export default ServiceDistributionDashboard;